/**
 * MSC Storage Compliance - Public API. Consent-aware storage with TTL.
 */

import type { ConsentCategory, MscKeyConfig, MscSetOptions } from './types';
import { isConsentGranted } from './consentBridge';
import { isExpired } from './envelope';
import { getAllConfigs, getKeyConfig, getPrefixConfigs } from './getKeyConfig';
import {
  cookieStorageGetValue,
  getMatchingKeys,
  removeKeyFromStorage,
  storageHasValue,
  storageReadEnvelopeWithFlag,
  storageRemove,
  storageSet,
} from './storage';
import { STORAGE_TYPES } from './types';

const DAYS_TO_MS = 24 * 60 * 60 * 1000;
const daysToMs = (days: number): number => days * DAYS_TO_MS;

const isCookieStorage = (cfg: MscKeyConfig): boolean => cfg.storage === STORAGE_TYPES.COOKIES;

function safeExecute<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

function set(key: string, value: unknown, options?: MscSetOptions): boolean {
  const cfg = getKeyConfig(key);
  if (!cfg) return false;
  const enforceConsent = options?.enforceConsent ?? true;
  if (enforceConsent && !isConsentGranted(cfg.category, cfg.vendors)) return false;
  const ttlDays = options?.ttlDays ?? cfg.ttlDays;
  if (ttlDays <= 0) return false;
  const expireAtEpochMs = Date.now() + daysToMs(ttlDays);
  return safeExecute(() => storageSet(cfg, value, expireAtEpochMs), false);
}

function get<T = unknown>(key: string): T | null {
  const cfg = getKeyConfig(key);
  if (!cfg) return null;
  if (!isConsentGranted(cfg.category, cfg.vendors)) return null;
  return safeExecute(() => {
    if (isCookieStorage(cfg)) {
      const value = cookieStorageGetValue(cfg);
      return (value ?? null) as T | null;
    }
    const { exists, envelope, raw } = storageReadEnvelopeWithFlag(cfg);
    if (!exists) return null;
    if (!envelope) {
      // Backward compatibility: legacy raw values (pre-envelope) should not crash reads.
      // Try to parse as JSON; if not JSON, treat as string.
      if (raw === null) return null;
      try {
        return JSON.parse(raw) as T;
      } catch {
        return raw as unknown as T;
      }
    }
    if (isExpired(envelope)) {
      storageRemove(cfg);
      return null;
    }
    return (envelope.value ?? null) as T | null;
  }, null);
}

function remove(key: string): void {
  const cfg = getKeyConfig(key);
  if (cfg) safeExecute(() => storageRemove(cfg), undefined);
}

function clearCategory(category: ConsentCategory): void {
  getAllConfigs()
    .filter((cfg) => cfg.category === category && !cfg.isPrefix)
    .forEach((cfg) => safeExecute(() => storageRemove(cfg), undefined));
}

function enforce(): void {
  const allConfigs = getAllConfigs();

  const shouldRemoveNonPrefix = (cfg: MscKeyConfig): boolean => {
    if (cfg.isPrefix) return false;
    if (isCookieStorage(cfg)) {
      const exists = storageHasValue(cfg);
      if (!exists) return false;
      return !isConsentGranted(cfg.category, cfg.vendors);
    }
    const { exists, envelope, raw } = storageReadEnvelopeWithFlag(cfg);
    if (!exists) return false;
    if (!envelope) {
      // Legacy raw value found (pre-envelope). If consent is granted and TTL is valid,
      // migrate it into an envelope instead of deleting on refresh.
      if (!isConsentGranted(cfg.category, cfg.vendors)) return true;
      if (cfg.ttlDays <= 0) return true;
      const expireAtEpochMs = Date.now() + daysToMs(cfg.ttlDays);
      if (raw !== null) {
        const legacyValue = safeExecute(() => JSON.parse(raw) as unknown, raw);
        safeExecute(() => storageSet(cfg, legacyValue, expireAtEpochMs), false);
      }
      return false;
    }
    if (!isConsentGranted(cfg.category, cfg.vendors)) return true;
    if (isExpired(envelope)) return true;
    return false;
  };

  allConfigs
    .filter(shouldRemoveNonPrefix)
    .forEach((cfg) => safeExecute(() => storageRemove(cfg), undefined));

  const prefixConfigs = getPrefixConfigs();
  for (const cfg of prefixConfigs) {
    if (isConsentGranted(cfg.category, cfg.vendors)) continue;
    const matchingKeys = getMatchingKeys(cfg.pattern, cfg.storage);
    for (const realKey of matchingKeys) {
      safeExecute(() => removeKeyFromStorage(realKey, cfg.storage), undefined);
    }
  }
}

function onConsentChanged(): void {
  let cookiesWereRemoved = false;
  const allConfigs = getAllConfigs();

  for (const cfg of allConfigs) {
    if (cfg.isPrefix) continue;
    if (isConsentGranted(cfg.category, cfg.vendors)) continue;
    if (!storageHasValue(cfg)) continue;
    if (cfg.storage === STORAGE_TYPES.COOKIES) cookiesWereRemoved = true;
    safeExecute(() => storageRemove(cfg), undefined);
  }

  const prefixConfigs = getPrefixConfigs();
  for (const cfg of prefixConfigs) {
    if (isConsentGranted(cfg.category, cfg.vendors)) continue;
    const matchingKeys = getMatchingKeys(cfg.pattern, cfg.storage);
    for (const realKey of matchingKeys) {
      if (cfg.storage === STORAGE_TYPES.COOKIES) cookiesWereRemoved = true;
      safeExecute(() => removeKeyFromStorage(realKey, cfg.storage), undefined);
    }
  }

  if (cookiesWereRemoved) {
    window.location.reload();
  }
}

export const mscStorage = {
  set,
  get,
  remove,
  clearCategory,
  enforce,
  onConsentChanged,
} as const;
